/**
 * <ScrollReveal /> — wrap any element to fade + slide it into view when it
 * crosses the viewport threshold. Uses native IntersectionObserver.
 *
 * v34 (Mar 2026): replaces the previous css-only `animate-fade-in-up` which
 * triggered on PAGE LOAD only (visitors scrolling never saw it). This one
 * fires per-element as the visitor scrolls — giving the page life.
 *
 * Props:
 *   - direction: 'up' | 'left' | 'right'   — which side it slides from
 *   - delay: milliseconds   — staggered group reveals
 *   - distance: pixels      — how far it travels (default 24px, subtle)
 *   - once: boolean         — only reveal once (default true) vs every entry
 *
 * Performance: a single IntersectionObserver per element, disconnects after
 * first reveal when `once` is true. No animation library, no re-renders.
 */
import React, { useEffect, useRef, useState } from 'react';

const ScrollReveal = ({
  children,
  direction = 'up',
  delay = 0,
  distance = 24,
  className = '',
  once = true,
  threshold = 0.15,
  as: Tag = 'div',
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Respect reduced-motion preference
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -40px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  // Translation vector based on direction
  const transform = (() => {
    if (visible) return 'translate3d(0, 0, 0)';
    switch (direction) {
      case 'left': return `translate3d(-${distance}px, 0, 0)`;
      case 'right': return `translate3d(${distance}px, 0, 0)`;
      case 'down': return `translate3d(0, -${distance}px, 0)`;
      case 'up':
      default: return `translate3d(0, ${distance}px, 0)`;
    }
  })();

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform,
        transition: `opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: visible ? 'auto' : 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
};

export default ScrollReveal;
